import React, { Component } from 'react';
import {connect} from "react-redux";
import {updateGame} from "../../actions/index";

import { BOUNDS } from "../../constants/Game";

import {firebaseBoundsToGoogle, googleBoundsToFirebaseBounds, northAmerica, zoomFromBounds} from "../../helper_functions";

import "../../global.css";
import './UpdateBoundsOnMap.css'
import ReactDOM from "react-dom";
import {GoogleApiWrapper} from "google-maps-react";
import boundsIcon from "../../img/twotone_map_black_24dp.png";

/**
 * A google map, on which user clicks to set either (a) clue location and clue bounds or (b) game bounds.
 * Pass in forClue={true} as a property if this is the clue case.
 *
 * Can go inside a modal.
 *
 * Also updates existing bounds and/or location manipulating a lat-lon bounding rect and/or a marker on google map.
 * Uses the global state - currently selected game for state, unless callback boundsRectCallback passed in.
 *
 * In the case of callbacks, the containing component tracks the state of the bounds (and clue location).
 *
 * PROPS
 *
 * width height  The dimensions of this component
 * google        Reference to google maps lib
 * bounds        The bounds box to display on the map.  Can be null for no bounds.
 * forClue       This bounds map is for a clue (as opposed to for a game).  If it's for a clue, user should also include
 *               clueMarkerCallback, boundsRectCallback, and, if marker has been set, the existing clueLat and clueLon.
 * clueLat       latitude of exact clue location
 * clueLon       longitude of exact clue location
 * hasDefaultLatLon  whether latDefault and lonDefault are present.
 * latDefault lonDefault  The default coordinates at which to display map if bounds not present
 * boundsForScale  Use these bounds to calculate scale.  If absent, scale is computed from regular bounds, or, if none, a default.
 * allowClear      If set to true, show a "Clear Rect" button allowing user to clear current bounds.
 * title   Title to display above the object.  (Otherwise, "Treasure Hunt Bounds")
 * closeModalCallback  Callback for when modal containing this object is closed.
 * boundsRectCallback  Callback for reference to bounds object created or being manipulated by the user.  If
 *    this callback is specified, it is assumed the parent object is doing the updating.  If not, this
 *    object updates the current game ref with these bounds.
 * clueMarkerCallback  Like boundsRectCallback, but for the clue and, unlike for bounds, this is the only way to track
 *                clue marker location.
 */
class UpdateBoundsOnMap extends Component {
   constructor(props) {
      super(props);

      // local state pertains to google map objects we need class wide access to
      this.state = {
         googleMap: null,
         googleRect: null,
         clueMarker: null,           // Only used for clue.
         userMarker: null
      };

      // bind the methods to this where we need the right 'this' inside anonymous inner classes.
      this.addClueMarkerToMap = this.addClueMarkerToMap.bind(this);
      this.updateUserMarker = this.updateUserMarker.bind(this);
      this.addBoundsRectToMap = this.addBoundsRectToMap.bind(this);
      this.googleBoundsFromProps = this.googleBoundsFromProps.bind(this);
      this.googleBoundsFromProps = this.googleBoundsFromProps.bind(this);
      this.updateBoundsForGame = this.updateBoundsForGame.bind(this);
      this.mapClickedToPlaceBounds = this.mapClickedToPlaceBounds.bind(this);
      this.clearBounds = this.clearBounds.bind(this);
   }

   // Create and Attach google map to dom
   componentDidMount() {
      const { google, forClue, bounds, clueLat, clueLon, outsideBounds } = this.props; // sets props equal to google

      // Get google bounds from props or default
      const gBounds = this.googleBoundsFromProps();

      // Get google zoom
      const gZoom = this.googleZoomFromPropsAndBounds(gBounds);

      // Build and attach google map
      const mapConfig = Object.assign({}, {
         center: gBounds.getCenter(),
         zoom: gZoom,
         streetViewControl: false
      });
      const mapRef = this.refs.map;                 // looks for HTML div ref 'map'. Returned in render below.
      const node = ReactDOM.findDOMNode(mapRef);    // finds the 'map' div in the React DOM, names it node
      this.map = new google.maps.Map(node, mapConfig); // creates a new Google map on the specified node (ref='map') with the specified configuration set above.
      this.setState( { googleMap: this.map} );

      // If bounds were present, show the bounds we determined as an editable rectangle.
      let hasBounds = !!bounds;
      const hasClueLatLon = !!clueLat && !!clueLon;
      // If marker is present, add it to the map.
      if (hasClueLatLon) {
         this.addClueMarkerToMap(clueLat, clueLon, this.map, true)
      }

      if (hasBounds) {
         this.addBoundsRectToMap(gBounds, this.map, true);
      }

      if (outsideBounds) {
         const googleRect = UpdateBoundsOnMap.gameBoundingBoxToGoogle(outsideBounds, google);
         this.addHuntBoundsToClueMap(googleRect, this.map)
      }

      // Otherwise add click listener
      if (!hasClueLatLon || (!hasBounds && !forClue)) {
         this.map.addListener('click', this.mapClickedToPlaceBounds);
      }
   }

   shouldComponentUpdate(nextProps, nextState, nextContext) {

      // always update for state change
      if (this.state !== nextState) return true;


      // but not normally for props changes.  NOTE this can cause unexpected bugs.

      // if we are updating because of changed props,
      // do not update if there is no change to whether we have loc reading, because we use when we have loc reading
      // to update the bounds of the map.
      const hasLocReading = this.props.hasDefaultLatLon;
      const willHaveLocReading = nextProps.hasDefaultLatLon;
      const willHaveUserCoordinates = !!nextProps.userCoords;

      return (!hasLocReading && willHaveLocReading)  ||  willHaveUserCoordinates;
   }

   componentDidUpdate(prevProps, prevState, snapshot) {

      if (!prevProps.hasDefaultLatLon && this.props.hasDefaultLatLon) {
         const gBounds = this.googleBoundsFromProps();
         this.state.googleMap.fitBounds(gBounds, 0);
      }

      if (this.props.userCoords) {
         this.updateUserMarker( this.props.userCoords);
      }
   }

   updateUserMarker(coords) {
      const lat = coords.latitude;
      const lon = coords.longitude;
      const googleLatLng = new this.props.google.maps.LatLng(lat, lon);

      if (!this.state.userMarker) {

         const marker = new this.props.google.maps.Marker({
            position: googleLatLng,
            title: "Your location",
            draggable: false,
            clickable: false,
            icon: {
               path: this.props.google.maps.SymbolPath.CIRCLE,
               scale: 8,
               strokeWeight: 2,
               strokeColor: "#FFF",
               fillColor: "#58E",
               fillOpacity: 1
            },
            // label: '2'
         });

         marker.setMap(this.state.googleMap);

         this.setState( { userMarker: marker });
      }
      else {
         this.state.userMarker.setPosition(googleLatLng);
      }
   }

   googleBoundsFromProps(props) {
      const {google, bounds, hasDefaultLatLon, latDefault, lonDefault, boundsForScale} = this.props; // sets props equal to google

      // Get game bounds from props or default
      var gBounds;
      let hasBounds = !!bounds;
      let hasBoundsForScale = !!boundsForScale;
      if (hasBounds)
         gBounds = UpdateBoundsOnMap.gameBoundingBoxToGoogle(bounds, google);
      else if (hasBoundsForScale)
         gBounds = firebaseBoundsToGoogle(boundsForScale, google);
      else if (hasDefaultLatLon)
         gBounds = UpdateBoundsOnMap.defaultBoundingBoxForLocation(latDefault, lonDefault, google);
      else
         gBounds = northAmerica(google);

      return gBounds;
   }

   googleZoomFromPropsAndBounds(gBounds) {
      const {width, height} = this.props;

      const mapW = this.checkMapDimension(width);
      const mapH = this.checkMapDimension(height);

      const mapDim = {
         width: mapW,
         height: mapH
      };

      let hasBounds = !!this.props.bounds;

      let {clueLat, clueLon} = this.props;
      const hasClueLatLon = !!clueLat && !!clueLon;

      // zoom from bounds;    Lower numbers are zoomed further out.
      let zoom = zoomFromBounds(gBounds, mapDim);
      // zoom out one if we had bounds to begin win (but keep zoomed in if clue bounds is set)
      if (hasClueLatLon) {
         zoom = zoom - 0.5;
      } else if (hasBounds) {
         zoom--;
      }
      return zoom;
   }

   static gameBoundingBoxToGoogle(bounding_box, google) {
      if (bounding_box == null) {
         return null;
      }
      const {southwest, northeast} = bounding_box;

      return new google.maps.LatLngBounds(
         new google.maps.LatLng(southwest.latitude, southwest.longitude),
         new google.maps.LatLng(northeast.latitude, northeast.longitude)
      );
   }

   static defaultBoundingBoxForLocation(latDef, lonDef, google) {
      // make it about the size of a few US states.
      const west = lonDef - 0.05;
      const east = lonDef + 0.05;
      const north = latDef + 0.03;
      const south = latDef - 0.03;

      return new google.maps.LatLngBounds(
         new google.maps.LatLng(south, west),
         new google.maps.LatLng(north, east)
      );
   }

   // user clicked on empty map, so place a rectangle where they clicked.
   mapClickedToPlaceBounds(event) {

      const isClue = this.props.forClue;

      if ((isClue && this.state.clueMarker)
         || (!isClue && this.state.googleRect)) {

         return;  // Already set.  It has to be cleared before it can be re-added.
      }

      const { latLng } = event;
      const { zoomInOnSelection, mapChangeCallback } = this.props;

      if (isClue) {
         this.addClueMarkerToMap(latLng.lat(), latLng.lng(), this.state.googleMap);
      }

      // Make bounds width about 1/5 of map size.
      // if we already have a google rect, use those bounds.
      const mapBounds =  this.state.googleMap.getBounds();
      const ne = mapBounds.getNorthEast();
      const sw = mapBounds.getSouthWest();

      const boundsToMapRatio = zoomInOnSelection ? 10 : 2.5;

      const lonWidth = (ne.lng() - sw.lng())/boundsToMapRatio;
      const latHeight = (ne.lat() - sw.lat())/boundsToMapRatio;

      const latDiff = latHeight/2;
      const lonDiff = lonWidth/2;

      // calculate rect, but just use existing rect if we already have it (case where we started with a rect, but no clue location).
      const rect = this.state.googleRect ? this.state.googleRect.getBounds() :
      {
         north: latLng.lat() + latDiff,
         south: latLng.lat() - latDiff,
         east: latLng.lng() + lonDiff,
         west: latLng.lng() - lonDiff
      };
      this.addBoundsRectToMap(rect, this.state.googleMap);
      if (!!mapChangeCallback) {
         mapChangeCallback();
      }

      if (zoomInOnSelection) {
         const padding = this.state.googleMap.getDiv().offsetHeight / 4;

         const {google} = this.props;

         const asBounds = new google.maps.LatLngBounds(
            new google.maps.LatLng(rect.south, rect.west),
            new google.maps.LatLng(rect.north, rect.east)
         );

         this.state.googleMap.fitBounds(asBounds, padding);
      }
   }

   // time to set a new lat-lon bounds on firebase and (hence) our app-wide state object
   updateBoundsForGame() {
      const { game, closeModalCallback, updateGame } = this.props;

      const bounds = this.state.googleRect.getBounds();
      const values = !bounds
         ?  { [BOUNDS]: null }
         :  { [BOUNDS]: googleBoundsToFirebaseBounds(bounds) };

      updateGame(game.id, game.isPublic, values, () => {
         closeModalCallback();
      });
   }

   addHuntBoundsToClueMap(googleRect, map) {

      const color = '#007000';
      const boundsRect = new this.props.google.maps.Rectangle({
         bounds: googleRect,
         strokeColor: color,
         strokeOpacity: 0.9,
         strokeWeight: 2.2,
         fillColor: color,
         fillOpacity: 0.07,
         clickable: false
      });

      boundsRect.setMap(map);
   }

   // turns lat-lon bounds into the Rectangle shape on the map.
   addBoundsRectToMap(rect, map, startup = false) {

      // we don't want the bounds to be editable if there is no location yet, so that user can click in there.
      const hasClueLocation = !!this.state.clueMarker || (!!this.props.clueLat && !!this.props.clueLon);
      const forGameBounds = !this.props.forClue;

      const boundsRect = new this.props.google.maps.Rectangle({
         bounds: rect,
         strokeColor: '#000000',
         strokeOpacity: 0.9,
         strokeWeight: 2.3,
         fillColor: '#000000',
         fillOpacity: 0.15,
         editable: hasClueLocation || forGameBounds,
         draggable: hasClueLocation || forGameBounds,
         clickable: hasClueLocation || forGameBounds
      });

      // allow parent to tract (get values from) bounds rect.
      const { boundsRectCallback } = this.props;

      if (!!this.props.mapChangeCallback) {
         // Add an event listener on the rectangle.
         boundsRect.addListener('bounds_changed', (event) => { this.props.mapChangeCallback(); });
      }

      boundsRect.setMap(map);

      this.setState({ googleRect : boundsRect});

      if (boundsRectCallback) {
         boundsRectCallback(boundsRect, startup);
      }
   }

   addClueMarkerToMap(lat, lon, map, startup = false) {
      var googleLatLng = new this.props.google.maps.LatLng(lat, lon);

      var marker = new this.props.google.maps.Marker({
         position: googleLatLng,
         title: 'Clue is here!',
         draggable: true,
         icon: {
            url: "https://maps.google.com/mapfiles/ms/icons/lightblue.png"
         },
         // label: '2'
      });

      if (!!this.props.mapChangeCallback) {
         // Add an event listener on the rectangle.
         // marker.addListener('drag', handleEvent);
         // marker.addListener('dragend', handleEvent);
         marker.addListener('dragend', (_) => { this.props.mapChangeCallback(); });
      }

      marker.setMap(map);

      this.setState( { clueMarker: marker });

      // allow parent to tract (get values from) marker.
      const { clueMarkerCallback } = this.props;

      if (clueMarkerCallback) {
         clueMarkerCallback(marker, startup);
      }
   }

   clearBounds() {
      if (this.state.googleRect) {
         this.state.googleRect.setMap(null);
         this.setState({ googleRect : null});
      }
      if (this.state.clueMarker) {
         this.state.clueMarker.setMap(null);
         this.setState( {clueMarker: null})
      }

      const { boundsRectCallback } = this.props;
      if (boundsRectCallback) {
         boundsRectCallback(null);
      }
      const { clueMarkerCallback } = this.props;
      if (clueMarkerCallback) {
         clueMarkerCallback(null);
      }
      this.map.addListener('click', this.mapClickedToPlaceBounds);
   }

   checkMapDimension(dim) {
      const fallBack = 100;

      if (dim == null) return fallBack;
      if (isNaN(dim)) return fallBack;
      if (dim < fallBack) return fallBack;
      if (dim > 2000) return fallBack;

      return dim;
   }

   render() {
      const { width, height, allowClear, title, forClue } = this.props;

      const mapW = this.checkMapDimension(width);
      const mapH = this.checkMapDimension(height);

      const style = {
         // width: `100%`,
         width: `${mapW}px`,
         height: `${mapH}px`,
      };
      const noTopPadding = {
         paddingTop: '0px'
      };

      const isModal = !!this.props.closeModalCallback;
      const isParentTrackingState = !!this.props.boundsRectCallback;

      const showClearButton = allowClear && this.state.googleRect;

      const hasTitle = !!title;

      // Intructional text above map.
      const directions = this.state.clueMarker
         ?  "Drag marker to set exact clue location (hidden to user). \nDrag and resize rectangle for clue bounds (shown)."
         : this.state.googleRect ?
             forClue ? "Click spot to add clue location." : "Drag and resize to get exact bounds."
                                 : "Click near hunt location to get started.";

      const directionsColorClass = "text-danger"
         // (!this.state.clueMarker) ? "text-danger" : "text-danger small";

      return (
         <div>
            {isModal ?
               <button
                  style={noTopPadding}
                  className="btn btn-link pull-right bottom-margin"
                  onClick={this.props.closeModalCallback}>

                  X
               </button>
               :  ""
            }

            {hasTitle &&
               <h5> <img className="title-icon" src={boundsIcon} alt="has bounds indicator"/> {title} </h5>
            }

            {/*<div align="center">*/}
            <div>
               <div style={style} className="map-image" ref={"map"}/>
               <div className={directionsColorClass}> {directions} </div>
               <div>
                  { !isParentTrackingState
                     ? <button
                        className="top-margin"
                        disabled={ !this.state.googleRect }
                        onClick={this.updateBoundsForGame}>
                        Save
                     </button>
                     : ""
                  }
                  { showClearButton
                     ? <button
                        className="top-margin"
                        onClick={this.clearBounds}>

                        Clear Bounds
                     </button>
                     : ""
                  }
               </div>
            </div>
         </div>
      );
   }
}

export function centerOfBounds(bounding_box) {
   const {southwest, northeast} = bounding_box;

   return { centerLat: (southwest.latitude  + northeast.latitude) /2,
      centerLon: (southwest.longitude + northeast.longitude)/2}
}


// boilerplate redux, tie in our app state to props
function mapStateToProps(state) {
   const selectedGame = state.games[state.selectedGameId];
   return {
      game: selectedGame
   }
}
export default connect(mapStateToProps, { updateGame } )(GoogleApiWrapper({
   apiKey: 'AIzaSyDAAYp_Ao7KMwxYxJFiIbutXuz4yyjeey8',
})(UpdateBoundsOnMap));



// WEBPACK FOOTER //
// ./src/components/huntcomponents/UpdateBoundsOnMap.js